import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { UserRole } from '@docsystem/shared';
import { authStore } from '@/lib/auth-store';
import { disconnectSocket, refreshSocketAuth } from '@/lib/socket';
import { ApiRequestError } from '@/lib/api-client';
import { authService } from '@/services';

export function useSession() {
  const state = useSyncExternalStore(
    (listener) => authStore.subscribe(listener),
    () => authStore.getState(),
    () => authStore.getState(),
  );

  // The socket handshake carries the access token, so a refresh must re-auth the
  // live connection or it keeps running on a credential the server will reject.
  useEffect(() => {
    if (state.accessToken) refreshSocketAuth();
  }, [state.accessToken]);

  return {
    user: state.user,
    clinic: state.clinic,
    isAuthenticated: Boolean(state.accessToken && state.user),
    role: state.user?.role ?? null,
    doctorId: state.user?.doctorId ?? null,
    isAdmin: state.user?.role === UserRole.ADMIN,
    isReceptionist: state.user?.role === UserRole.RECEPTIONIST,
    isDoctor: state.user?.role === UserRole.DOCTOR,
  };
}

export function useLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      authService.login(username, password),
    onSuccess: (data) => {
      authStore.setSession(data.accessToken, data.user, data.clinic);
      queryClient.clear();
      navigate(landingRouteFor(data.user.role), { replace: true });
      toast.success(`Welcome back, ${data.user.fullName.split(' ')[0]}`);
    },
    onError: (error) => {
      const message =
        error instanceof ApiRequestError ? error.message : 'Sign-in failed. Please try again.';
      toast.error(message);
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      // A failed revoke must not trap the user in a session they asked to leave.
    }
    authStore.clear();
    disconnectSocket();
    queryClient.clear();
    navigate('/login', { replace: true });
  }, [navigate, queryClient]);
}

/**
 * Silently restores a session on a full page load.
 *
 * The access token is memory-only by design, so after a refresh the app has no
 * credential — but the httpOnly refresh cookie is still there, and one call to
 * /auth/refresh brings the session back without showing a login screen.
 */
export function useRestoreSession(): { restoring: boolean } {
  const state = useSyncExternalStore(
    (listener) => authStore.subscribe(listener),
    () => authStore.getState(),
    () => authStore.getState(),
  );

  const restore = useMutation({
    mutationFn: async () => {
      const me = await authService.me();
      return me;
    },
  });

  useEffect(() => {
    if (state.accessToken || restore.isPending || restore.isSuccess || restore.isError) return;

    // `api.get` triggers the refresh-and-replay path on 401, so this single call
    // both re-authenticates and loads the principal.
    restore.mutate(undefined, {
      onSuccess: (data) => {
        const token = authStore.accessToken;
        if (token) authStore.setSession(token, data.user, data.clinic);
      },
    });
  }, [state.accessToken, restore]);

  return { restoring: restore.isPending };
}

export function landingRouteFor(role: string): string {
  switch (role) {
    case UserRole.ADMIN:
      return '/admin/dashboard';
    case UserRole.RECEPTIONIST:
      return '/reception/dashboard';
    case UserRole.DOCTOR:
      return '/doctor/dashboard';
    default:
      return '/login';
  }
}
